/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICartViewProps, IOrderSummaryErrors } from './lds-cart';
import { ICartlinesViewProps } from './components/cart-line-items';
import { ICartLineItemViewProps } from './components/cart-line-items-promotion';
//@ts-ignore
import { Primary, Ghost } from '@churchofjesuschrist/eden-buttons';
//@ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

const _renderCartlines = (
    cartLines: ICartlinesViewProps[] | undefined,
    cartEmptyText: string,
    CartlinesWrapper: INodeProps,
    storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean,
    adobeData?: any
): JSX.Element[] | JSX.Element => {
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map(cartLine => {
            const AdobeAnalyticRemove = (removeFunction: any) => {
                // Adobe Analytics for Remove from Cart
                if (cartLine.data && cartLine.data.product) {
                    const { Name, Dimensions, ItemId } = cartLine.data.product;
                    const language = Dimensions?.filter((dimension: any) => {
                        return dimension.DimensionTypeValue === 4;
                    });
                    const Style = Dimensions?.filter((dimension: any) => {
                        return dimension.DimensionTypeValue === 1;
                    });
                    const Size = Dimensions?.filter((dimension: any) => {
                        return dimension.DimensionTypeValue === 3;
                    });
                    const productLang = language && language[0] && language[0].DimensionValue ? language[0].DimensionValue.Value : '';
                    const productStyle = Style && Style[0] && Style[0].DimensionValue ? Style[0].DimensionValue.Value : '';
                    const productSize = Size && Size[0] && Size[0].DimensionValue ? Size[0].DimensionValue.Value : '';
                    const skuNum = adobeData.filter((item: any) => {
                        return (
                            item.info.id === ItemId &&
                            item.info.language === productLang &&
                            item.info.style === productStyle &&
                            item.info.size === productSize
                        );
                    });
                    // @ts-ignore
                    if (window && window['digitalData']) {
                        // @ts-ignore
                        window['digitalData'].push({
                            event: 'Remove from Cart',
                            product: [
                                {
                                    info: {
                                        sku: skuNum[0] ? skuNum[0].info.sku : '',
                                        id: ItemId,
                                        name: Name,
                                        language: productLang,
                                        style: productStyle,
                                        size: productSize
                                    }
                                }
                            ]
                        });
                    }
                }
                removeFunction();
            };
            return (
                <div className='msc-cart-lines-item' key={cartLine.cartlineId}>
                    {cartLine.cartline}
                    {storeSelector &&
                    cartLine.pickUpInStore &&
                    cartLine.data &&
                    cartLine.data.cartline.ExtensionProperties &&
                    cartLine.data.cartline.ExtensionProperties[0] &&
                    cartLine.data.cartline.ExtensionProperties[0].Value?.BooleanValue !== true ? (
                        <Node {...cartLine.pickUpInStore.ContainerProps}>{cartLine.pickUpInStore.defaultComponent}</Node>
                    ) : null}
                    {cartLine.addToOrderTemplate ? (
                        <Node className='msc-cart-line__extra-actions'>
                            {/* {cartLine.remove} */}
                            <button
                                // @ts-ignore
                                className={cartLine.remove.props.className}
                                data-exp-event-id='CartPage.Lds cart.Remove.RemoveFromCart'
                                // @ts-ignore
                                onClick={() => AdobeAnalyticRemove(cartLine.remove.props.onClick)}
                            >
                                {/* @ts-ignore */}
                                {cartLine.remove.props.children}
                            </button>
                            {/* {cartLine.addToWishlist} */}
                            {cartLine.addToOrderTemplate}
                        </Node>
                    ) : (
                        <>
                            {/* {cartLine.remove} */}
                            <button
                                // @ts-ignore
                                className={cartLine.remove.props.className}
                                data-exp-event-id='CartPage.Lds cart.Remove.RemoveFromCart'
                                // @ts-ignore
                                onClick={() => AdobeAnalyticRemove(cartLine.remove.props.onClick)}
                            >
                                {/* @ts-ignore */}
                                {cartLine.remove.props.children}
                            </button>
                            {/* {cartLine.addToWishlist} */}
                        </>
                    )}
                </div>
            );
        });
    }

    return cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <p className='msc-cart-line'>{cartEmptyText}</p>
            {backToShoppingButton}
        </div>
    ) : (
        <>{waitingComponent}</>
    );
};

/**
 * Method will render error block.
 * @param errorData -The order summary errors interface.
 * @returns Jsx element.
 */
const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
        return null;
    }
    return (
        // <Node {...errorData.Wrapper}>
        <Error style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}>
            <div style={{ paddingTop: '3px' }}>{errorData.errors}</div>
        </Error>
        // {/* {errorData.header} */}
        // {/* {errorData.errors} */}
        // </Node>
    );
};

/**
 * Method will render invoice summary lines.
 * @param invoiceSummaryLines -The invoicesummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Method will render order summary lines.
 * @param orderSummaryLines -The ordersummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {/* {props.promoCode} */}
            {orderSummaryLines.subtotal}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            {orderSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Method will render cart promotions.
 * @param promotions -The cartline item view props.
 * @returns Jsx element.
 */
const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines group title.
 * @param cartLine -The cartline view props.
 * @param titleSeparate -The title separate.
 * @returns Jsx element.
 */
const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, titleSeparate: string) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    return (
        <>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
                        {`${cartLine.pickUpInStore?.deliveryOption}${titleSeparate} ${cartLine.pickUpInStore?.orgUnitName}`}
                    </p>
                ) : null}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines groups header title.
 * @param pickUpText -The pickup text for group.
 * @param shippingText -The shipping text for group.
 * @param emailShippingText -The email shipping text for group.
 * @param titleSeparate -The title separator.
 * @param cartLine -The cartline view props.
 * @param emailDeliveryModeCode -The email delivery code from channel.
 * @returns Jsx element.
 */
const renderCartLinesGroupHeader = (
    pickUpText: string,
    deliveryText: string,
    shippingText: string,
    emailShippingText: string,
    titleSeparate: string,
    cartLine: ICartlinesViewProps,
    emailDeliveryModeCode?: string
) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailShippingText : groupTitle;

    const titleStyle = groupTitle.toLocaleLowerCase() === deliveryText.toLocaleLowerCase() ? 'shipping' : 'pickup';

    return (
        <>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${titleStyle}-icon`} />
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${titleStyle}`}>{groupTitle}</p>
                {renderCartLinesGroupTitle(cartLine, titleSeparate)}
            </div>
        </>
    );
};

/**
 * Method will render cartlines in groups.
 * @param props -The cartline.
 * @returns Jsx element.
 */
const renderCartLinesGroup = (props: ICartViewProps, adobeData?: any): JSX.Element | undefined => {
    if (props.cartLinesGroup && ArrayExtensions.hasElements(props.cartLinesGroup)) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    return (
                        <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                            {renderCartLinesGroupHeader(
                                props.resources.pickUpText,
                                props.resources.headingImages,
                                props.resources.shippingText,
                                props.resources.emailshippingText,
                                props.resources.titleSeparate,
                                cartlines[0],
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                            {_renderCartlines(
                                cartlines,
                                props.resources.emptyCartText,
                                props.CartlinesWrapper,
                                props.storeSelector,
                                props.backToShoppingButton,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                adobeData
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    return props.cartDataResult ? (
        <div className='msc-cartline-wraper'>
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                {/* {props.backToShoppingButton} */}
                {/*@ts-ignore */}
                <Primary {...props.backToShoppingButton.props} />
            </div>
        </div>
    ) : (
        <>{props.waitingComponent}</>
    );
};

const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [cartDeliveryMatch, setCartDeliveryMatch] = React.useState<boolean[] | undefined>([true]);
    const [checkoutProducts, setCheckoutProducts] = React.useState<any>([]);
    const [subscriptionError, setSubscriptionError] = React.useState<any>(false);

    const onClick = (value: boolean) => {
        setIsVisible(value);
    };
    const hasTrueBooleanValue = (cartLine: any) => {
        for (const item of cartLine) {
            const extensionProperties = item.data.product.ExtensionProperties;
            for (const property of extensionProperties) {
                if (property.Value.BooleanValue === true) {
                    return true;
                }
            }
        }
        return false;
    };
    const checkoutValidation = async () => {
        if (props.cartlines) {
            const isBopis = props.cartlines[0].pickUpInStore === undefined ? false : props.cartlines[0].pickUpInStore?.isBopisSelected;
            const storeName = props.cartlines[0].pickUpInStore === undefined ? '' : props.cartlines[0].pickUpInStore.orgUnitName;
            const isSubscriptions = hasTrueBooleanValue(props.cartlines);
            setSubscriptionError(isSubscriptions);

            const matching = props.cartlines.map((item, index) => {
                const itemBopis = item.pickUpInStore?.isBopisSelected === undefined ? false : item.pickUpInStore.isBopisSelected;
                const itemStoreName = item.pickUpInStore === undefined ? '' : item.pickUpInStore.orgUnitName;

                if (itemBopis === isBopis && itemStoreName === storeName) {
                    return true;
                } else {
                    return false;
                }
            });

            setCartDeliveryMatch(matching);
        }
    };
    const getSku = async (Product: any) => {
        const ctx = { callerContext: props.context.actionContext };
        const { RecordId, Name, Dimensions, Price, ItemId } = Product.product;

        const productAttributes = await getAttributeValuesAsync(ctx, RecordId, props.context.request.channel!.RecordId, 0);
        const language = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 4;
        });
        const Style = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 1;
        });
        const Size = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 3;
        });
        let fulfillableSku;
        if (productAttributes) {
            fulfillableSku = productAttributes.filter((item: any) => {
                return item.RecordId === 5637144592;
            });
        }

        const adobeItem = {
            info: {
                sku: fulfillableSku ? fulfillableSku[0].TextValue : '',
                id: ItemId,
                name: Name,
                language: language && language[0] && language[0].DimensionValue ? language[0].DimensionValue.Value : '',
                style: Style && Style[0] && Style[0].DimensionValue ? Style[0].DimensionValue.Value : '',
                size: Size && Size[0] && Size[0].DimensionValue ? Size[0].DimensionValue.Value : ''
            },
            quantity: Product.cartline.Quantity,
            price: {
                basePrice: Price
            }
        };

        setCheckoutProducts((prevState: any) => [...prevState, adobeItem]);
    };
    React.useEffect(() => {
        if (props.cartlines) {
            props.cartlines.map((item: any) => {
                void getSku(item.data);
            });
        }
    }, []);

    const AdobeAnalyticsCheckout = async () => {
        // @ts-ignore
        if (window && window['digitalData']) {
            // @ts-ignore
            window['digitalData'].push({
                event: 'Checkout',
                cart: { item: checkoutProducts }
            });
        }
        sessionStorage.setItem('taxIdCheck', 'false');
        sessionStorage.setItem('idNum', '');
        sessionStorage.setItem('TaxId', '');
        sessionStorage.setItem('taxIdRegion', '');
        sessionStorage.setItem('emailOnCart', 'false');

        // @ts-ignore
        window.location.assign(props.checkoutAsSignInUserButton.props.href);
    };

    React.useEffect(() => {
        void checkoutValidation();
        // setCartDeliveryMatch(testing);
    }, [props.cartlines]);

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            {props.title}
            {!props.cart?.hasInvoiceLine && props.promotionOptions && _renderPromotions(props.promotionOptions)}
            {props.multiplePickUpEnabled ? (
                renderCartLinesGroup(props, checkoutProducts)
            ) : (
                <Node {...props.CartlinesWrapper}>
                    {_renderCartlines(
                        props.cartlines,
                        props.resources.emptyCartText,
                        props.CartlinesWrapper,
                        props.storeSelector,
                        props.backToShoppingButton,
                        props.waitingComponent,
                        props.cartLoadingStatus,
                        props.cartDataResult,
                        checkoutProducts
                    )}
                </Node>
            )}
            {props.orderSummaryHeading && (
                <Node {...props.OrderSummaryWrapper}>
                    {props.orderSummaryHeading}
                    {props.cart?.hasInvoiceLine
                        ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                        : _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                    {_renderErrorBlock(props.OrderSummaryErrors)}
                    {/* {props.checkoutAsSignInUserButton} */}
                    {isVisible && (
                        <Error onClose={() => onClick(false)} style={{ width: '100%', marginTop: '10px' }}>
                            {props.resources.cartCheckoutFailedMessage}
                        </Error>
                    )}
                    {subscriptionError && (
                        <Error style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}>
                            {props.resources.checkoutDisabledSubscriptionText}
                        </Error>
                    )}
                    {cartDeliveryMatch?.includes(false) ? (
                        <Primary onClick={() => onClick(true)} fullWidth={true}>
                            {props.resources.checkoutButtonTitle}
                        </Primary>
                    ) : (
                        <Primary
                            // @ts-ignore
                            disabled={props.checkoutAsSignInUserButton.props.disabled || subscriptionError}
                            // @ts-ignore
                            // href={props.checkoutAsSignInUserButton.props.href}
                            onClick={() => AdobeAnalyticsCheckout()}
                            // @ts-ignore
                            title={props.checkoutAsSignInUserButton.props.title}
                            fullWidth={true}
                        >
                            {/*@ts-ignore */}
                            {props.checkoutAsSignInUserButton.props.children}
                        </Primary>
                    )}
                    {/* {props.checkoutAsGuestButton} */}
                    {/* {props.backToShoppingButton} */}
                    <Ghost
                        // @ts-ignore
                        href={props.backToShoppingButton.props.href}
                        // @ts-ignore
                        title={props.backToShoppingButton.props.title}
                        fullWidth={true}
                        style={{ fontSize: '16px', marginTop: '15px' }}
                    >
                        {/*@ts-ignore */}
                        {props.backToShoppingButton.props.children}
                    </Ghost>
                    {props.createTemplateFromCartButton}
                </Node>
            )}
            {props.storeSelector}
        </div>
    );
};

export default CartView;
